import { useEffect, useState, useRef, useCallback } from "react";
import "./Bbs.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import * as noticeFormatDate from "../../../../components/Util/DateFormat";
import { getServerBaseUrl } from "../../../../utils/portConfig";
import LockIcon from "../../../../assets/icons/lock-01.svg";

const PatientCommunity = ({ fromMokLok }) => {
  const [displayedData, setDisplayedData] = useState([]); // 화면에 표시할 데이터
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [keyword, setKeyword] = useState("");

  // 무한 스크롤 관련 상태
  const [page, setPage] = useState(0);
  const observer = useRef();

  // 한 번에 로드할 아이템 수
  const ITEMS_PER_LOAD = 5;

  const API_SERVER_V2_URL = `${getServerBaseUrl()}/api/v2`; // API 서버 URL
  const token = localStorage.getItem("token"); // 토큰
  const auth = localStorage.getItem("auth"); // 권한

  const navigate = useNavigate();

  // 무한 스크롤을 위한 마지막 아이템 참조 설정
  const lastElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          // 스크롤이 마지막 요소에 도달하면 다음 데이터 로드
          loadMoreData();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  // 스크롤 시 추가 데이터 로드 함수
  const loadMoreData = () => {
    if (loading || !hasMore) return;
    setLoading(true);

    // 다음 페이지 데이터 요청
    const nextPage = page + 1;
    fetchCommunityData(nextPage);
  };

  useEffect(() => {
    // 컴포넌트가 마운트될 때 커뮤니티 게시글 목록
    getCommunityCategories();
    fetchCommunityData(0);
  }, []);

  const getCommunityCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_SERVER_V2_URL}/community/categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = response.data.data;
      if (data.length > 0) {
        setCategories(data);
      }
    } catch (error) {
      console.error("카테고리 데이터 가져오기 실패:", error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // 페이지별 데이터 가져오기
  const fetchCommunityData = async (pageNum, selectedCategory) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_SERVER_V2_URL}/community/list`,
        {
          page: pageNum,
          size: ITEMS_PER_LOAD,
          keyword: keyword || null,
          filters: {
            category: selectedCategory || null,
          },
          sort: {
            createdDt: "desc",
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response.data.data;
      const count = response.data.data.count;

      if (data.content.length > 0) {
        // 기존 데이터에 새 데이터 추가
        if (pageNum === 0) {
          setDisplayedData(data.content);
        } else {
          setDisplayedData((prev) => [...prev, ...data.content]);
        }

        setPage(pageNum);
        // 더 불러올 데이터가 있는지 확인 (현재까지 로드된 항목 수 < 총 항목 수)
        setHasMore((pageNum + 1) * ITEMS_PER_LOAD < count);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("커뮤니티 데이터 가져오기 실패:", error);
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleError = (error) => {
    if (error.response) {
      const responseData = error.response.data;
      if (responseData) {
        Swal.fire({
          icon: "error",
          title: "데이터 로딩 중 오류 발생",
          text: responseData.message,
        });
      } else {
        console.error("서버 응답 오류:", error.response.status);
      }
    } else {
      console.error("서버 응답이 없습니다.");
    }
  };

  const navigateTo = (item) => {
    const fromMokLok = "상세(환자_커뮤니티)";
    navigate(auth === "Superadmin" || auth === "Admin" ? `/admin/qna/detail` : window.location.pathname, {
      state: {
        setFromMokLok: fromMokLok,
        setNoticeDetailId: item.id,
        isCommunity: "환자_커뮤니티",
        category: getCategoryName(item.category),
      },
    });
  };

  const getCategoryName = (categoryName) => {
    if (!categoryName) return "기타";

    const category = categories.find((cat) => cat.key === categoryName);
    return category?.name ? category.name : "기타";
  };

  // Enter 키로 검색
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      keywordRefreshData();
    }
  };

  const keywordRefreshData = async () => {
    setDisplayedData([]);
    setPage(0);
    setHasMore(true);
    await fetchCommunityData(0);
  };

  return (
    <div className={"bbs-table-container"} style={{ maxHeight: auth === "Superadmin" || auth === "Admin" ? "100vh" : "calc(100vh - 129px)" }}>
      <div className={"search-field-container"}>
        <div className={"search-field"}>
          <label>분류</label>
          <select
            className={"dashboard-content-select"}
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              fetchCommunityData(0, e.target.value);
            }}
          >
            <option value="">분류 선택</option>
            {categories.map((category, index) => (
              <option key={index} value={category.key}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
        <div className={"search-field"}>
          <label>키워드</label>
          <input className={"keyword-field"} type="text" placeholder="키워드 입력" value={keyword} onChange={(e) => setKeyword(e.target.value)} onKeyPress={handleKeyPress} />
          <button className={"green-button"} onClick={keywordRefreshData}>
            검색
          </button>
        </div>
      </div>

      <div className="bbs-wrapper">
        <div className="bbs-custom-table">
          <div className="row header4 bbs-header-box">
            <div className="cell">번호</div>
            <div className="cell-title">제목</div>
            <div className="cell">작성자</div>
            <div className="cell">카테고리</div>
            <div className="cell">작성일</div>
            {/* <div className="cell">조회 수</div> */}
          </div>
          {displayedData.length > 0 && (
            <>
              {displayedData.map((communityItem, index) => (
                <div className={`${index % 2 === 0 ? "row row-hovered" : "row-colored bbs-click-style row-hovered"}`} key={index} ref={index === displayedData.length - 1 ? lastElementRef : null}>
                  <div className="cell text-center">{`${communityItem.id}`}</div>
                  <div className="cell bbs-click-style" style={{ alignItems: "center" }} onClick={() => navigateTo(communityItem)}>
                    <div className="bbs-table-title">
                      {`${communityItem.title}`}
                      {communityItem.privatePost && <img src={LockIcon} />}
                    </div>
                  </div>
                  <div className="cell text-center">{`${communityItem.nickname ?? "-"}`}</div>
                  <div className="cell text-center">{`${getCategoryName(communityItem.category)}`}</div>
                  <div className="cell text-center">{`${noticeFormatDate.formatDateWbar(communityItem.createdDt) || ""}`}</div>
                  {/* <div className="cell text-right">{`${communityItem.viewsCount}`}</div> */}
                </div>
              ))}
            </>
          )}
        </div>
        {displayedData.length === 0 && !loading && (
          <div className="no-data-indicator">
            <div>데이터가 없습니다.</div>
          </div>
        )}
        {loading && (
          <div className="loading-indicator">
            <div>로딩 중...</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientCommunity;
